import React, { useRef, useEffect } from 'react';
import * as constantsViewer from '../../constants';
import { useSpring, a } from 'react-spring/three';
import { Pane } from '../pieces/Pane';
import { Batten } from '../pieces/Batten';
import { CrossPiece } from '../pieces/CrossPiece';
import { onPointerDown, onPointerUp } from '../../helpers';
import { useDispatch } from 'react-redux';
import { applyPaintColor } from 'containers/PaintPanel/actions';

export function Rack({
  cabinetWidth,
  cabinetDepth,
  rack,
  cabinetId,
  shelfLevel,
  configuratorMode,
  position = [0, 0, 0],
  unselected = false,
}) {
  const group = useRef();

  const [spring, set] = useSpring(() => ({
    position: position,
    immediate: !constantsViewer.REACT_SPRING_ANIMATIONS_ACTIVE,
    config: constantsViewer.REACT_SPRING_ANIMATIONS_CONF,
  }));
  useEffect(() => {
    let self = group.current;
    if (self) {
      set({
        position: position,
      });
    }
  });
  return (rack.battens && rack.battens.length > 0) ||
    (rack.panes && rack.panes.length > 0) ||
    (rack.crossPieces && rack.crossPieces.length > 0) ||
    rack.plate ? (
    <a.group ref={group} position={spring.position}>
      {rack.battens && rack.battens.length > 0 ? (
        <RackBattens
          cabinetWidth={cabinetWidth}
          cabinetDepth={cabinetDepth}
          rack={rack}
          unselected={unselected}
        />
      ) : null}

      {rack.panes && rack.panes.length > 0 ? (
        <RackSides
          cabinetWidth={cabinetWidth}
          cabinetDepth={cabinetDepth}
          rack={rack}
          cabinetId={cabinetId}
          shelfLevel={shelfLevel}
          configuratorMode={configuratorMode}
          unselected={unselected}
        />
      ) : null}

      {(rack.crossPieces && rack.crossPieces.length > 0) || rack.plate ? (
        <RackBottom
          cabinetWidth={cabinetWidth}
          cabinetDepth={cabinetDepth}
          rack={rack}
          cabinetId={cabinetId}
          shelfLevel={shelfLevel}
          configuratorMode={configuratorMode}
          unselected={unselected}
        />
      ) : null}
    </a.group>
  ) : (
    <></>
  );
}

function RackBattens({ cabinetWidth, cabinetDepth, rack, unselected = false }) {
  const rackHeight = rack.realHeight - 40;

  const group = useRef();

  const [spring, set] = useSpring(() => ({
    scale: [1, rackHeight / 370, 1],
    immediate: !constantsViewer.REACT_SPRING_ANIMATIONS_ACTIVE,
    config: constantsViewer.REACT_SPRING_ANIMATIONS_CONF,
  }));

  useEffect(() => {
    let self = group.current;
    if (self) {
      set({
        scale: [1, rackHeight / 370, 1],
      });
    }
  });

  return (
    <a.group ref={group} scale={spring.scale}>
      {rack.battens.map(b => {
        return (
          <Batten
            battenPosition={b.position}
            cabinetWidth={cabinetWidth}
            cabinetDepth={cabinetDepth}
            unselected={unselected}
            key={b.position}
          />
        );
      })}
    </a.group>
  );
}

function RackSides({
  cabinetWidth,
  cabinetDepth,
  rack,
  cabinetId,
  shelfLevel,
  configuratorMode,
  unselected = false,
}) {
  const rackHeight = rack.realHeight - 40;

  const group = useRef();

  const [spring, set] = useSpring(() => ({
    scale: [1, (370 * (rackHeight / 370) + 10) / 380, 1],
    immediate: !constantsViewer.REACT_SPRING_ANIMATIONS_ACTIVE,
    config: constantsViewer.REACT_SPRING_ANIMATIONS_CONF,
  }));

  useEffect(() => {
    let self = group.current;
    if (self) {
      set({
        scale: [1, (370 * (rackHeight / 370) + 10) / 380, 1],
      });
    }
  });

  return (
    <a.group ref={group} scale={spring.scale}>
      {rack.panes.map(p => {
        return (
          <Pane
            pane={p}
            cabinetId={cabinetId}
            shelfLevel={shelfLevel}
            configuratorMode={configuratorMode}
            rackLevel={rack.level}
            pPosition={p.position}
            cabinetWidth={cabinetWidth}
            cabinetDepth={cabinetDepth}
            unselected={unselected}
            key={p.position}
          />
        );
      })}
    </a.group>
  );
}

function RackBottom({
  cabinetWidth,
  cabinetDepth,
  rack,
  cabinetId,
  shelfLevel,
  configuratorMode,
  unselected = false,
}) {
  const rackHeight = rack.realHeight - 40;

  const group = useRef();

  const [spring, set] = useSpring(() => ({
    position: [0, -((rackHeight - 370) / 2), 0],
    immediate: !constantsViewer.REACT_SPRING_ANIMATIONS_ACTIVE,
    config: constantsViewer.REACT_SPRING_ANIMATIONS_CONF,
  }));

  useEffect(() => {
    let self = group.current;
    if (self) {
      set({
        position: [0, -((rackHeight - 370) / 2), 0],
      });
    }
  });

  return (
    <a.group ref={group} position={spring.position}>
      {rack.crossPieces.map(cp => {
        return (
          <CrossPiece
            crossPiece={cp}
            configuratorMode={configuratorMode}
            rack={rack}
            cabinetWidth={cabinetWidth}
            cabinetDepth={cabinetDepth}
            unselected={unselected}
            key={cp.position}
          />
        );
      })}

      {rack.plate && (
        <Pane
          pane={rack.plate}
          cabinetId={cabinetId}
          shelfLevel={shelfLevel}
          configuratorMode={configuratorMode}
          rackLevel={rack.level}
          pPosition="U"
          cabinetWidth={cabinetWidth}
          cabinetDepth={cabinetDepth}
          unselected={unselected}
        />
      )}

      {rack.glassPlate && (
        <Pane
          pane={rack.glassPlate}
          pPosition="UG"
          cabinetWidth={cabinetWidth}
          cabinetDepth={cabinetDepth}
          unselected={unselected}
        />
      )}
    </a.group>
  );
}
